import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { bookingService, talentService, userService } from '../../../api/apiService';
import confirmIcon from '../../../images/confirm.svg';
import declineIcon from '../../../images/closered.svg';
import pauseIcon from '../../../images/onhold.svg';
import swapIcon from '../../../images/swap.svg';
import { customStyles } from './../../../helpers/StyleUtils';

const ListViewProposed = ({ events, holdevents, authToken, initialWeek }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedKPI, setSelectedKPI] = useState("");
  const [selectedGrup, setSelectedGrup] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedSet, setSelectedSet] = useState("");
  const [isKindMode, setIsKindMode] = useState(false);
  const processedGroups = new Set();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  let userEmail = localStorage.getItem("userEmail");
  const [filterPreferences, setFilterPreferences] = useState([]);
  const [selectedPreferenceIndex, setSelectedPreferenceIndex] = useState(null); 
  const [currentWeek, setCurrentWeek] = useState(initialWeek || getCurrentWeek());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const combinedEvents = [...events, ...holdevents];

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!userEmail) return;

      try {
        const response = await userService.getUserPreferences(userEmail);
        if (response.data && response.data.filterPreferences) {
          setFilterPreferences(response.data.filterPreferences || []);
        } else {
          // console.log("No filter preferences found.");
        }
      } catch (error) {
        console.error("Error fetching user preferences:", error);
      }
    };

    fetchPreferences();
  }, [userEmail]);

  // Effect to handle KIND mode toggle
  useEffect(() => {
    if (isKindMode) {
      // When KIND mode is ON, set to "digital Production"
      setSelectedSet({ value: 'digital Production', label: 'digital Production' });
    } else {
      // When KIND mode is OFF, clear the filter
      setSelectedSet("");
    }
  }, [isKindMode]);

  const saveFilterPreference = async (preferenceIndex) => {
    const newPreference = {
      searchTerm,
      selectedClient,
      selectedPosition,
      selectedSet,
      selectedLocation,
      selectedUser,
    };

    // Determine whether to update an existing preference or add a new one
    if (typeof preferenceIndex === 'number') {
      if (preferenceIndex < filterPreferences.length) {
        filterPreferences[preferenceIndex] = newPreference;
      }
    } else if (filterPreferences.length < 3) {
      filterPreferences.push(newPreference);
    } else {
      alert('You can only save up to 3 filter preferences.');
      return;
    }

    try {
      // Update filter preferences in the API
      await userService.savePreferences(userEmail, { filterPreferences });
      setFilterPreferences([...filterPreferences]); // Update local state
      // Removed success alert - preferences saved silently
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save filter preferences.');
    }
  };

  const applyFilterPreference = (index) => {
    if (index < 0 || index >= filterPreferences.length) {
      // console.log("Invalid filter preference index.");
      return;
    }

    const preference = filterPreferences[index];
    setSearchTerm(preference.searchTerm);
    setSelectedClient(preference.selectedClient);
    setSelectedPosition(preference.selectedPosition);
    setSelectedSet(preference.selectedSet);
    setSelectedLocation(preference.selectedLocation);
    setSelectedUser(preference.selectedUser);
  };

  const preferenceOptions = filterPreferences.map((_, index) => ({ value: index, label: `Preference ${index + 1}` }));
  const handlePreferenceChange = selectedOption => {
    setSelectedPreferenceIndex(selectedOption ? selectedOption.value : null);
    if (selectedOption) {
      applyFilterPreference(selectedOption.value);
    } else {
      // Clear all filter preferences here
      setSearchTerm("");
      setSelectedClient(null);
      setSelectedPosition(null);
      setSelectedSet(null);
      setSelectedLocation(null);
      setSelectedUser(null);
    }
  };
  
  const saveOrUpdateFilterPreference = async () => {
    const newPreference = {
      searchTerm,
      selectedClient,
      selectedPosition,
      selectedSet,
      selectedLocation,
      selectedUser,
    };
  
    if (selectedPreferenceIndex !== null) {
      // Update an existing preference
      filterPreferences[selectedPreferenceIndex] = newPreference;
    } else {
      // Save a new preference if there's room
      if (filterPreferences.length < 3) {
        filterPreferences.push(newPreference);
      } else {
        alert('You can only save up to 3 filter preferences.');
        return;
      }
    }
  
    try {
      // Update filter preferences in the API
      await userService.savePreferences(userEmail, { filterPreferences });
      setFilterPreferences([...filterPreferences]); // Update local state
      // Removed success alert - preferences updated silently
    } catch (error) {
      console.error('Error updating preferences:', error);
      alert('Failed to update filter preferences.');
    }
  };
  
  const handleChange = event => {
    setSearchTerm(event.target.value);
  };

  const fetchBooking = async (bookingId) => {
    try {
      const response = await bookingService.getBookingById(bookingId);
      // The booking data is directly in the response.data
      if (response.data) {
        return response.data;
      }
      console.error("Booking not found");
      return null;
    } catch (error) {
      console.error("Error fetching booking:", error);
      return null;
    }
  };

  // Fetch talent based on talentId
  const fetchTalent = async (talentId) => {
    if (!talentId) {
      console.error("Invalid talent ID provided:", talentId);
      return null;
    }
    
    try {
      // console.log("Fetching talent with ID:", talentId);
      const response = await talentService.getTalentById(talentId);
      // console.log("Talent API response:", response);
      // console.log("Response.data:", response.data);
      // console.log("Response.data keys:", response.data ? Object.keys(response.data) : "No data");
      
      // Handle different API response formats (same as ListViewRequested.js)
      if (response && response.data) {
        // Case 1: { talent: {...} }
        if (response.data.talent) {
          // console.log("Talent found in response.data.talent:", response.data.talent);
          return response.data.talent;
        }
        // Case 2: Direct talent data in response.data
        else if (response.data.id || response.data.Name || response.data.E_Mail) {
          // console.log("Talent found in response.data:", response.data);
          return response.data;
        }
        // Case 3: Check if data itself is the talent object
        else if (typeof response.data === 'object' && response.data !== null) {
          // console.log("Checking if response.data itself is talent data:", response.data);
          // Check for common talent fields
          if (response.data.name || response.data.Name || response.data.email || response.data.E_Mail) {
            // console.log("Talent found in response.data (direct object):", response.data);
            return response.data;
          }
        }
      }
      
      console.error("Talent not found for ID:", talentId, "Response structure:", {
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        responseKeys: Object.keys(response || {}),
        fullResponse: response
      });
      return null;
    } catch (error) {
      console.error("Error fetching talent:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return null;
    }
  };
  
  const acceptporposedtalent = async (bookingId, groupId) => {
    try {
      // Determine which bookings to update
      const bookingsToUpdate = groupId ?
        combinedEvents.filter(event => event.group === groupId) :
        combinedEvents.filter(event => event.id === bookingId);

      if (bookingsToUpdate.length === 0) {
        alert("No bookings found to update.");
        return;
      }

      // Update each booking
      for (const bookingEvent of bookingsToUpdate) {
        try {
          // Update booking status
          await bookingService.updateBookingStatus(bookingEvent.id, 'confirmed');
          
          // Get the talent info if needed for notification
          const talent = await fetchTalent(bookingEvent.talent_id);
          
          if (talent) {
            // Send notification
            await sendNotification(bookingEvent.id, {
              user_id: bookingEvent.proposedBy,
              message: `Planning has confirmed talent ${talent.name} for ${bookingEvent.client}`,
              booking_id: bookingEvent.id,
              url: `admin/detail?id=${bookingEvent.id}`
            });
          }
        } catch (error) {
          console.error(`Error updating booking ${bookingEvent.id}:`, error);
          alert(`Error updating booking ${bookingEvent.id}. Please try again.`);
          return;
        }
      }

      // Reload page after successful operation
      window.location.reload();
    } catch (error) {
      console.error("Error in acceptporposedtalent:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    }
  };

  const hold = async (bookingId, groupId) => {
    try {
      const booking = await fetchBooking(bookingId);
      if (!booking) {
        alert("Booking not found. Please try again.");
        return;
      }

      // Find the event in combinedEvents to get talent information
      const event = combinedEvents.find(e => e.id === bookingId);
      if (!event) {
        alert("Event not found in current view. Please try again.");
        return;
      }

      // Try different possible talent ID fields
      const talentId = event.talent_id || event.talentId || event.talent?.id || event.talent?.Id;
      if (!talentId) {
        console.error("No talent ID found in event:", event);
        alert("Talent ID not found in booking data. Please try again.");
        return;
      }

      // console.log("Fetching talent with ID:", talentId);
      const talent = await fetchTalent(talentId);
      if (!talent) {
        console.error("Talent not found for ID:", talentId);
        alert("Talent information not found. Please try again.");
        return;
      }

      // Determine which bookings to update
      const bookingsToUpdate = groupId ?
        combinedEvents.filter(event => event.group === groupId) :
        combinedEvents.filter(event => event.id === bookingId);

      if (bookingsToUpdate.length === 0) {
        alert("No bookings found to update.");
        return;
      }

      // Update each booking
      for (const bookingEvent of bookingsToUpdate) {
        try {
          // Update booking status
          await bookingService.updateBookingStatus(bookingEvent.id, 'hold');
          
          // Send notification
          await sendNotification(bookingEvent.id, {
            user_id: bookingEvent.proposedBy,
            message: `Planning has put order on hold`,
            booking_id: bookingEvent.id,
            url: `admin/detail?id=${bookingEvent.id}`
          });
        } catch (error) {
          console.error(`Error updating booking ${bookingEvent.id}:`, error);
          alert(`Error updating booking ${bookingEvent.id}. Please try again.`);
          return;
        }
      }

      // Reload page after successful operation
      window.location.reload();
    } catch (error) {
      console.error("Error in hold function:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    }
  };

  const askAnotherTalent = async (bookingId, groupId) => {
    try {
      const booking = await fetchBooking(bookingId);
      if (!booking) {
        alert("Booking not found. Please try again.");
        return;
      }

      // Find the event in combinedEvents to get talent information
      const event = combinedEvents.find(e => e.id === bookingId);
      if (!event) {
        alert("Event not found in current view. Please try again.");
        return;
      }

      // Try different possible talent ID fields
      const talentId = event.talent_id || event.talentId || event.talent?.id || event.talent?.Id;
      if (!talentId) {
        console.error("No talent ID found in event:", event);
        alert("Talent ID not found in booking data. Please try again.");
        return;
      }

      // console.log("Fetching talent with ID:", talentId);
      const talent = await fetchTalent(talentId);
      if (!talent) {
        console.error("Talent not found for ID:", talentId);
        alert("Talent information not found. Please try again.");
        return;
      }

      // Determine which bookings to update
      const bookingsToUpdate = groupId ?
        combinedEvents.filter(event => event.group === groupId) :
        combinedEvents.filter(event => event.id === bookingId);

      if (bookingsToUpdate.length === 0) {
        alert("No bookings found to update.");
        return;
      }

      // Process each booking
      const updatePromises = bookingsToUpdate.map(async (bookingEvent) => {
        try {
          // Update existing booking to 'new order'
          await bookingService.updateBookingStatus(bookingEvent.id, 'new order');
          
          // Create new booking for the talent on hold
          const newBookingData = {
            status: 'talentOnHold',
            talent_id: bookingEvent.talentId || bookingEvent.talent_id,
            start_date: bookingEvent.start_date,
            end_date: bookingEvent.end_date || bookingEvent.start_date,
            title: bookingEvent.title || bookingEvent.boktitle,
            kpi: bookingEvent.kpi,
            set: bookingEvent.set,
            position: bookingEvent.position,
            client: { value: 'Set Cast 1. Option', label: 'Set Cast 1. Option' },
            range: bookingEvent.range || '',
            option: bookingEvent.option,
            proposed_by: bookingEvent.proposedBy,
            start_time: bookingEvent.startTime,
            end_time: bookingEvent.endTime,
            swap: true,
            old_talent: talent.name
          };
          
          await bookingService.createBooking(newBookingData);
          
          // Send notification
          await sendNotification(bookingEvent.id, {
            user_id: bookingEvent.proposedBy,
            message: `Planning has asked for another talent for ${bookingEvent.client}`,
            booking_id: bookingEvent.id,
            url: `admin/detail?id=${bookingEvent.id}`
          });
        } catch (error) {
          console.error(`Error processing booking ${bookingEvent.id}:`, error);
          throw new Error(`Failed to process booking ${bookingEvent.id}`);
        }
      });

      await Promise.all(updatePromises);
      // Reload page after successful operation
      window.location.reload();
    } catch (error) {
      console.error("Error in askAnotherTalent:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    }
  };

  const declineproposedtalent = async (bookingId, groupId) => {
    try {
      const booking = await fetchBooking(bookingId);
      if (!booking) {
        alert("Booking not found. Please try again.");
        return;
      }

      // Find the event in combinedEvents to get talent information
      const event = combinedEvents.find(e => e.id === bookingId);
      if (!event) {
        alert("Event not found in current view. Please try again.");
        return;
      }

      // Try different possible talent ID fields
      const talentId = event.talent_id || event.talentId || event.talent?.id || event.talent?.Id;
      if (!talentId) {
        console.error("No talent ID found in event:", event);
        alert("Talent ID not found in booking data. Please try again.");
        return;
      }

      // console.log("Fetching talent with ID:", talentId);
      const talent = await fetchTalent(talentId);
      if (!talent) {
        console.error("Talent not found for ID:", talentId);
        alert("Talent information not found. Please try again.");
        return;
      }

      // Determine which bookings to update
      const bookingsToUpdate = groupId ?
        combinedEvents.filter(event => event.group === groupId) :
        combinedEvents.filter(event => event.id === bookingId);

      if (bookingsToUpdate.length === 0) {
        alert("No bookings found to update.");
        return;
      }

      // Update each booking
      for (const bookingEvent of bookingsToUpdate) {
        try {
          // Update booking status
          await bookingService.updateBookingStatus(bookingEvent.id, 'optionSolved');
          
          // Send notification
          await sendNotification(bookingEvent.id, {
            user_id: bookingEvent.proposedBy,
            message: `Planning has canceled the talent ${talent.name} for ${booking.client?.label || booking.client}`,
            booking_id: bookingEvent.id,
            url: `admin/detail?id=${bookingEvent.id}`
          });
          
          // Send decline email
          await sendDeclineEmail(booking, talent);
        } catch (error) {
          console.error(`Error updating booking ${bookingEvent.id}:`, error);
          alert(`Error updating booking ${bookingEvent.id}. Please try again.`);
          return;
        }
      }

      // Reload page after successful operation
      window.location.reload();
    } catch (error) {
      console.error("Error in declineproposedtalent:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    }
  };

  // Helper function to send notifications
  const sendNotification = async (bookingId, notificationData) => {
    try {
      await bookingService.sendNotification(bookingId, notificationData);
      return true;
    } catch (error) {
      console.error("Error sending notification:", error);
      return false;
    }
  };

  // Helper function to send decline email
  const sendDeclineEmail = async (booking, talent) => {
    try {
      const emailData = {
        to: 'booking@set-cast.com, talent@mondula.com',
        subject: 'Planning Orendt has declined the booking',
        template: 'booking_declined',
        templateData: {
          talent_name: talent.name,
          booking_title: booking.title,
          start_date: booking.start_date,
          end_date: booking.end_date,
          position: booking.position?.label || booking.position,
          kpi: (() => {
            const kpiValue = booking.kpi;
            if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
              return 'Not specified';
            }
            
            // Handle objects (like location objects that might be in the wrong field)
            if (typeof kpiValue === 'object' && kpiValue !== null) {
              return 'Not specified';
            }
            
            // Convert to number and round to integer
            const numericValue = parseFloat(kpiValue);
            if (!isNaN(numericValue)) {
              return Math.round(numericValue);
            }
            
            // If it's not a valid number, return 'Not specified'
            return 'Not specified';
          })(),
          client: booking.client?.label || booking.client
        }
      };
      
      await bookingService.sendEmail(emailData);
      return true;
    } catch (error) {
      console.error("Error sending decline email:", error);
      return false;
    }
  };

  const createmailcomfirmed = async (filteredBooking, talent, start) => {
    try {
      const emailData = {
        to: 'booking@set-cast.com, talent@mondula.com',
        subject: 'Planning Orendt has accepted the booking',
        template: 'booking_confirmed',
        templateData: {
          talent_name: talent.name,
          booking_title: filteredBooking.title,
          start_date: filteredBooking.start_date,
          end_date: filteredBooking.end_date,
          position: filteredBooking.position?.label || filteredBooking.position,
          kpi: (() => {
            const kpiValue = filteredBooking.kpi;
            if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
              return 'Not specified';
            }
            
            // Handle objects (like location objects that might be in the wrong field)
            if (typeof kpiValue === 'object' && kpiValue !== null) {
              return 'Not specified';
            }
            
            // Convert to number and round to integer
            const numericValue = parseFloat(kpiValue);
            if (!isNaN(numericValue)) {
              return Math.round(numericValue);
            }
            
            // If it's not a valid number, return 'Not specified'
            return 'Not specified';
          })(),
          client: filteredBooking.client?.label || filteredBooking.client,
          location: (() => {
            const locationValue = filteredBooking.location;
            if (locationValue === null || locationValue === undefined || locationValue === '') {
              return 'Not specified';
            }
            
            // Handle location as an object
            if (typeof locationValue === 'object' && locationValue !== null) {
              return locationValue.name || locationValue.short || 'Not specified';
            }
            
            // Handle location as a string
            if (typeof locationValue === 'string') {
              return locationValue;
            }
            
            return 'Not specified';
          })()
        }
      };
      
      await bookingService.sendEmail(emailData);
      return true;
    } catch (error) {
      console.error("Error sending confirmation email:", error);
      return false;
    }
  };

  const createmailtalentcomfirmed = async (filteredBooking, talent, start) => {
    try {
      const emailData = {
        to: `${talent.email}, booking@set-cast.com, talent@mondula.com`,
        subject: 'Planning Orendt has accepted the booking',
        template: 'booking_confirmed_talent',
        templateData: {
          talent_name: talent.name,
          booking_title: filteredBooking.title,
          start_date: filteredBooking.start_date,
          end_date: filteredBooking.end_date,
          position: filteredBooking.position?.label || filteredBooking.position,
          kpi: (() => {
            const kpiValue = filteredBooking.kpi;
            if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
              return 'Not specified';
            }
            
            // Handle objects (like location objects that might be in the wrong field)
            if (typeof kpiValue === 'object' && kpiValue !== null) {
              return 'Not specified';
            }
            
            // Convert to number and round to integer
            const numericValue = parseFloat(kpiValue);
            if (!isNaN(numericValue)) {
              return Math.round(numericValue);
            }
            
            // If it's not a valid number, return 'Not specified'
            return 'Not specified';
          })(),
          client: filteredBooking.client?.label || filteredBooking.client
        }
      };
      
      await bookingService.sendEmail(emailData);
      return true;
    } catch (error) {
      console.error("Error sending talent confirmation email:", error);
      return false;
    }
  };

  const createmaildeclined = async (filteredBooking, talent, start) => {
    try {
      const emailData = {
        to: 'booking@set-cast.com, talent@mondula.com',
        subject: 'Planning Orendt has declined the booking',
        template: 'booking_declined',
        templateData: {
          talent_name: talent.name,
          booking_title: filteredBooking.title,
          start_date: filteredBooking.start_date,
          end_date: filteredBooking.end_date,
          position: filteredBooking.position?.label || filteredBooking.position,
          kpi: (() => {
            const kpiValue = filteredBooking.kpi;
            if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
              return 'Not specified';
            }
            
            // Handle objects (like location objects that might be in the wrong field)
            if (typeof kpiValue === 'object' && kpiValue !== null) {
              return 'Not specified';
            }
            
            // Convert to number and round to integer
            const numericValue = parseFloat(kpiValue);
            if (!isNaN(numericValue)) {
              return Math.round(numericValue);
            }
            
            // If it's not a valid number, return 'Not specified'
            return 'Not specified';
          })(),
          client: filteredBooking.client?.label || filteredBooking.client
        }
      };
      
      await bookingService.sendEmail(emailData);
      return true;
    } catch (error) {
      console.error("Error sending decline email:", error);
      return false;
    }
  };

  function getWeekNumber(d) {
    // Copy date so don't modify original
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    // Get first day of year
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  }

  const createTask = async (booking, talent) => {
    // console.log('create called' + authToken)
    // add entity - POST
    var myHeaders = new Headers();
    myHeaders.append("Authorization", "Bearer " + authToken);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append('Access-Control-Allow-Origin', 'http://dev-setcast.mondula.com/');
    myHeaders.append('Access-Control-Allow-Credentials', 'true');
    const kw = 'KW' + getWeekNumber(new Date(booking.start_date.seconds * 1000)) + " ";
    var titile = kw + '_' + booking.client.label + '_' + booking.position.label + '_' + new Date(booking.start_date.seconds * 1000).toLocaleDateString().split('T')[0] + '-' + new Date(booking.end_date.seconds * 1000).toLocaleDateString().split('T')[0];

    // Replace hardcoded data with the booking details
    var raw = JSON.stringify({
      "task": {
        "title": titile,
        "description": booking.description || ' ',
        "details": booking.description || ' ',
        "starts_on": new Date(booking.start_date.seconds * 1000).toLocaleDateString().split('T')[0],
        "ends_on": new Date(booking.end_date.seconds * 1000).toLocaleDateString().split('T')[0],
        "rate": talent.Tagessatz,
        "duration_in_time_units": booking.range,
        "currency": "EUR",

        "budget_setting": "set_budget",
        "assignee_id": talent.Lano_ID,
        "rate_type_id": 1,
        "creator_id": 216149,
        "state": "freelancer_assigned",
        "task_cost_centers_attributes": [
          {
            "cost_center_1_id": booking.costcenter1.id,
            "cost_center_2_id": booking.costcenter2.id
          }
        ]
      }
    });

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'manual'
    };

    fetch("https://api.lano.io/api/v1/tasks", requestOptions)
      .then(response => response.text())
      .then(result => { console.log(result); /* setMeddage(result); */ }) // Typo fix: it should probably be setMessage
      .catch(error => console.log('error', error));
  }

  const renderButtons = (event) => {
    const isGroupProcessed = event.group && processedGroups.has(event.group);
    if (!isGroupProcessed) {
      if (event.group) {
        processedGroups.add(event.group);
      }

      return (<>
        <a className='action-button' onClick={() => acceptporposedtalent(event.id, event.group)} style={{ marginRight: '10px' }}>
          <img src={confirmIcon} alt="Accept" title="Accept order" className="action-container" />
        </a>
        <a className='action-button' style={{marginLeft:20}} onClick={() => askAnotherTalent(event.id, event.group)}>
          <img style={{ width: '26px' }} src={swapIcon} alt="requestAnotherTalent" title="Swap talent" className="action-container" />
        </a>
        <a className='action-button' onClick={() => hold(event.id, event.group)} style={{ marginRight: '10px' }}>
          <img src={pauseIcon} alt="hold" title="Put on hold" className="action-container" />
        </a>
        <a className='action-button' style={{paddingLeft:'10px', marginRight: '10px'}} title="Cancel this order" onClick={() => declineproposedtalent(event.id, event.group)}>
          <img src={declineIcon} style={{color:'red'}} alt="CancelOrder" title="Decline order" className="action-container" />
        </a>
      </>);
    }
    return null;
  };

  const renderButtonsforHold = (event) => {
    const isGroupProcessed = event.group && processedGroups.has(event.group);
    if (!isGroupProcessed) {
      if (event.group) {
        processedGroups.add(event.group);
      }

      return (<>
        <a className='action-button' onClick={() => acceptporposedtalent(event.id, event.group)} style={{ marginRight: '10px' }}>
          <img src={confirmIcon} alt="Accept" title="Accept order" className="action-container" />
        </a>
        <a className='action-button' style={{marginLeft:20}} onClick={() => askAnotherTalent(event.id, event.group)} >
          <img style={{ width: '26px' }} src={swapIcon} alt="requestAnotherTalent" title="Swap talent" className="action-container" />
        </a>
        <a className='action-button' style={{paddingLeft:'10px', marginRight: '10px'}} title="Cancel this order" onClick={() => declineproposedtalent(event.id, event.group)}>
          <img src={declineIcon} alt="CancelOrder" title="Decline order" className="action-container" />
        </a>
      </>);
    }
    return null;
  };

  const handleKPIChange = option => {
    setSelectedKPI(option ? option.value : null);
  };
  
  const handleGroupChange = option => {
    setSelectedGrup(option ? option.value : null);
  };

  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null });
  
  const getUniqueKPIs = () => {
    const kpis = new Set();
    events.forEach(event => {
      if (event.kpi) kpis.add(event.kpi);
    });
    return Array.from(kpis).map(kpi => ({ label: kpi, value: kpi }));
  };

  const gerUniqueGroup = () => {
    const groups = new Set();
    events.forEach(event => {
      if (event.group) groups.add(event.group);
    });
    return Array.from(groups).map(group => ({ label: group, value: group }));
  };

  const getClients = () => {
    const clients = new Set();
    events.forEach(event => {
      if (event.client) clients.add(event.client);
    });
    return Array.from(clients).map(client => ({ label: client, value: client }));
  };

  const clientoption = getClients();
  const uniqueKPIs = getUniqueKPIs();
  const uniqueGroup = gerUniqueGroup();

  const set = [
    { value: 'On Model', label: 'On Model' },
    { value: 'On Produkt', label: 'On Produkt' },
    { value: 'digital Production', label: 'digital Production' },
  ];
  
  const getLocationDisplay = (event) => {
    if (!event) return '';
    
    // Handle location output formatting
    let locationText = '';
    
    // Check if location is an object
    if (event.location && typeof event.location === 'object') {
      // Access name property or short if name doesn't exist
      locationText = event.location.short || event.location.name || '';
    } else if (typeof event.location === 'string') {
      locationText = event.location;
    }
    
    return locationText;
  };

  // Helper function to get location short for filters
  const getLocationShort = (event) => {
    if (!event) return '';
    
    // Check if location is an object
    if (event.location && typeof event.location === 'object') {
      // Use short property for filters
      return event.location.short || event.location.name || '';
    } else if (typeof event.location === 'string') {
      return event.location;
    }
    
    return '';
  };

  const locationOptions = [...new Set(events.map(event => getLocationShort(event)).filter(Boolean))].map(location => ({ value: location, label: location }));
  const positionOptions = [...new Set(events.map(event => event.position).filter(Boolean))].map(position => ({ value: position, label: position }));

  // Create user options from multiple user fields in events
  const getUsersFromEvents = () => {
    const users = new Set();
    if (events && Array.isArray(events)) {
      events.forEach(event => {
        if (event.created_by) users.add(event.created_by);
        if (event.proposed_by) users.add(event.proposed_by);
        if (event.proposedBy) users.add(event.proposedBy);
        if (event.processing_by) users.add(event.processing_by);
      });
    }
    return Array.from(users).filter(user => user && user.trim() !== '')
      .map(user => ({ value: user, label: user }));
  };

  const userOptions = getUsersFromEvents();

  // Helper function to determine if a color is dark or light
  const isColorDark = (hexColor) => {
    if (!hexColor) return false;
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance using the formula for relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if dark (luminance < 0.5)
    return luminance < 0.5;
  };

  const filteredEvents = events.filter(event => {
    // Search term filter
    const matchesSearchTerm = 
      (event.boktitle && event.boktitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.set && event.set.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.talent && event.talent.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.status && event.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.company && event.company.toLowerCase().includes(searchTerm.toLowerCase()));

    // KPI filter
    const matchesKPI = selectedKPI ? event.kpi === selectedKPI : true;
    const matchesGroup = selectedGrup ? event.group === selectedGrup : true;

    // Client filter - check if selectedClient exists and has a value property
    const matchesClient = !selectedClient || !selectedClient.value || event.client === selectedClient.value;
    
    // Set filter - check if selectedSet exists and has a value property
    const matchesSet = !selectedSet || !selectedSet.value || event.set === selectedSet.value;
    
    // Location and Position filters
    const matchesLocation = !selectedLocation || getLocationShort(event) === selectedLocation.value;
    const matchesPosition = !selectedPosition || event.position === selectedPosition.value;

    // User filtering - check created_by, proposed_by, and processing_by fields
    const matchesUser = !selectedUser || (
      event.created_by === selectedUser.value ||
      event.proposed_by === selectedUser.value ||
      event.proposedBy === selectedUser.value ||
      event.processing_by === selectedUser.value
    );

    // Date range filter
    const eventDate2 = event.start instanceof Date ? event.start : new Date(event.start);
    const matchesDateRange = selectedDateRange.start && selectedDateRange.end ?
      (eventDate2 >= selectedDateRange.start && eventDate2 <= selectedDateRange.end) :
      true;

    return matchesSearchTerm && matchesKPI && matchesDateRange && matchesClient && matchesSet && matchesLocation && matchesPosition && matchesGroup && matchesUser;
  });

  const filteredholdEvents = holdevents.filter(event => {
    const matchesSearchTerm = 
      (event.boktitle && event.boktitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.set && event.set.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.talent && event.talent.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.status && event.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.company && event.company.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesKPI = selectedKPI ? event.kpi === selectedKPI : true;
    const matchesGroup = selectedGrup ? event.group === selectedGrup : true;
    
    // Updated client filter
    const matchesClient = !selectedClient || !selectedClient.value || event.client === selectedClient.value;
    
    // Updated set filter
    const matchesSet = !selectedSet || !selectedSet.value || event.set === selectedSet.value;
    
    const matchesLocation = !selectedLocation || getLocationShort(event) === selectedLocation.value;
    const matchesPosition = !selectedPosition || event.position === selectedPosition.value;

    // User filtering - check created_by, proposed_by, and processing_by fields
    const matchesUser = !selectedUser || (
      event.created_by === selectedUser.value ||
      event.proposed_by === selectedUser.value ||
      event.proposedBy === selectedUser.value ||
      event.processing_by === selectedUser.value
    );

    const eventDate2 = event.start instanceof Date ? event.start : new Date(event.start);
    const matchesDateRange = selectedDateRange.start && selectedDateRange.end ?
      (eventDate2 >= selectedDateRange.start && eventDate2 <= selectedDateRange.end) :
      true;

    return matchesSearchTerm && matchesKPI && matchesDateRange && matchesClient && matchesSet && matchesLocation && matchesPosition && matchesGroup && matchesUser;
  });

  const groupedAndSortedEvents = useMemo(() => {
    const grouped = filteredEvents.reduce((acc, event) => {
      const groupKey = event.group || 'No Group';
      acc[groupKey] = acc[groupKey] || [];
      acc[groupKey].push(event);
      return acc;
    }, {});

    for (const group in grouped) {
      grouped[group].sort((a, b) => {
        const dateA = a.start instanceof Date ? a.start : new Date(a.start);
        const dateB = b.start instanceof Date ? b.start : new Date(b.start);
        return dateA - dateB;
      });
    }

    return grouped;
  }, [filteredEvents]);

  // Group and sort the hold events
  const groupedAndSortedHoldEvents = useMemo(() => {
    const grouped = filteredholdEvents.reduce((acc, event) => {
      const groupKey = event.group || 'No Group';
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(event);
      return acc;
    }, {});

    // Sort events within each group by start date
    for (const group in grouped) {
      grouped[group].sort((a, b) => {
        const dateA = a.start instanceof Date ? a.start : new Date(a.start);
        const dateB = b.start instanceof Date ? b.start : new Date(b.start);
        return dateA - dateB;
      });
    }

    return grouped;
  }, [filteredholdEvents]);

  // Add this helper function after the const declarations at the top of the component
  const getStudioDisplay = (event) => {
    if (!event) return '';
    
    // Handle studio output formatting
    if (typeof event.studio === 'string') {
      return event.studio;
    } else if (event.studio && typeof event.studio === 'object') {
      return event.studio.name || '';
    }
    
    return '';
  };

  // Add these helper functions along with the previous ones
  const getClientDisplay = (event) => {
    if (!event) return '';
    
    if (event.client) {
      // Handle client as an object with label property
      if (typeof event.client === 'object' && event.client.label) {
        return event.client.label;
      }
      
      // Handle client as a string
      if (typeof event.client === 'string') {
        return event.client;
      }
    }
    
    return '';
  };

  const getPositionDisplay = (event) => {
    if (!event) return '';
    
    if (event.position) {
      // Handle position as an object with label property
      if (typeof event.position === 'object' && event.position.label) {
        return event.position.label;
      }
      
      // Handle position as a string
      if (typeof event.position === 'string') {
        return event.position;
      }
    }
    
    return '';
  };

  const getTalentDisplay = (event) => {
    if (!event) return '';
    
    // For KIND orders (identified by set = "digital Production"), show number of days from title
    if (event.set === "digital Production" && event.title) {
      const match = event.title.match(/(\d+)\s+day/);
      if (match) {
        return `${match[1]} day${match[1] === '1' ? '' : 's'}`;
      }
    }
    
    if (event.talent) {
      // Handle talent as an object with name property
      if (typeof event.talent === 'object' && event.talent.name) {
        return event.talent.name;
      }
      
      // Handle talent as a string
      if (typeof event.talent === 'string') {
        return event.talent;
      }
    }
    
    // Try talent_name field if talent is not available
    if (event.talent_name && typeof event.talent_name === 'string') {
      return event.talent_name;
    }
    
    return '';
  };

  // Add helper function for SonstigeInfo
  const getSonstigeInfoDisplay = (event) => {
    if (!event) return '';
    
    // Check different possible property names for additional info
    if (event.SonstigeInfo) {
      if (typeof event.SonstigeInfo === 'object') {
        return event.SonstigeInfo.text || event.SonstigeInfo.value || '';
      }
      return event.SonstigeInfo;
    }
    
    // Check alternative property names
    if (event.sonstige_info) {
      if (typeof event.sonstige_info === 'object') {
        return event.sonstige_info.text || event.sonstige_info.value || '';
      }
      return event.sonstige_info;
    }
    
    // Check for additional_info property
    if (event.additional_info) {
      if (typeof event.additional_info === 'object') {
        return event.additional_info.text || event.additional_info.value || '';
      }
      return event.additional_info;
    }
    
    return '';
  };

  // Helper for KIND-specific info (KIND orders only)
  const getKindInfoDisplay = (event) => {
    if (!event) return '';
    const kindInfo = event.kind_info || event.kindInfo || '';
    return typeof kindInfo === 'string' ? kindInfo : '';
  };

  // Add helper function for option field
  const getOptionDisplay = (event) => {
    if (!event) return '';
    
    if (event.option) {
      // Handle option as an object with label or value property
      if (typeof event.option === 'object') {
        return event.option.label || event.option.value || '';
      }
      
      // Handle option as a string
      if (typeof event.option === 'string') {
        return event.option;
      }
    }
    
    return '';
  };

  // Helper function to get current week number (ISO 8601)
  function getCurrentWeek() {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Set both dates to Thursday of their respective weeks
    // This ensures we get the correct ISO week number
    start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
    today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
    
    const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
    return weekNumber;
  }

  // Helper function to display KPI as integer
  const getKPIDisplay = (event) => {
    if (!event || !event.kpi) return "";
    
    const kpiValue = event.kpi;
    
    // Handle object KPI (shouldn't happen but just in case)
    if (typeof kpiValue === 'object' && kpiValue !== null) {
      return "";
    }
    
    // Convert to number and round to integer
    const numericValue = parseFloat(kpiValue);
    if (!isNaN(numericValue)) {
      return Math.round(numericValue).toString();
    }
    
    return "";
  };

  // Helper function to get ISO week number
  function getISOWeek(date) {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target) / 604800000);
  }

  // Helper function to get dates for a specific week and year
  function getWeekDates(weekNumber, year) {
    // Get January 4th of the year (this is always in week 1 according to ISO 8601)
    const jan4 = new Date(year, 0, 4);
    
    // Find the Monday of week 1
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
    
    // Calculate the Monday of the specified week
    const weekStart = new Date(mondayOfWeek1);
    weekStart.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
    
    // Calculate the Sunday of the specified week
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    return { weekStart, weekEnd, year: weekStart.getFullYear() };
  }

  // Navigation functions
  const goToPreviousWeek = () => {
    if (currentWeek === 1) {
      // Go to last week of previous year
      setCurrentYear(currentYear - 1);
      setCurrentWeek(52); // Most years have 52 weeks
    } else {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const goToNextWeek = () => {
    const { weekStart } = getWeekDates(currentWeek + 1, currentYear);
    const weekYear = weekStart.getFullYear();
    
    if (weekYear > currentYear) {
      // Moving to next year
      setCurrentYear(weekYear);
      setCurrentWeek(1);
    } else if (currentWeek >= 52) {
      // Check if year has 53 weeks
      const dec31 = new Date(currentYear, 11, 31);
      const lastWeek = getISOWeek(dec31);
      if (currentWeek < lastWeek) {
        setCurrentWeek(currentWeek + 1);
      } else {
        // Go to first week of next year
        setCurrentYear(currentYear + 1);
        setCurrentWeek(1);
      }
    } else {
      setCurrentWeek(currentWeek + 1);
    }
  };

  const goToCurrentWeek = () => {
    const now = new Date();
    setCurrentWeek(getISOWeek(now));
    setCurrentYear(now.getFullYear());
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={handleChange}
          className='inputtext search'
          style={{ flex: 1 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: '700', fontSize: '14px', textTransform: 'uppercase' }}>KIND</span>
          <label style={{ 
            position: 'relative', 
            display: 'inline-block', 
            width: '60px', 
            height: '34px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={isKindMode}
              onChange={() => setIsKindMode(!isKindMode)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
          <span style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isKindMode ? '#34C759' : '#ccc',
            transition: '0.4s',
            borderRadius: '34px',
            cursor: 'pointer'
          }}>
              <span style={{
                position: 'absolute',
                content: '""',
                height: '26px',
                width: '26px',
                left: isKindMode ? '30px' : '4px',
                bottom: '4px',
                backgroundColor: 'white',
                transition: '0.4s',
                borderRadius: '50%'
              }} />
            </span>
          </label>
        </div>
      </div>
      <div style={{ marginTop: '15px', marginBottom: '10px', display: 'flex', }}>
        <Select
          value={preferenceOptions.find(option => option.value === selectedPreferenceIndex)}
          onChange={handlePreferenceChange}
          options={preferenceOptions}
          isClearable
          placeholder="Select Preference"
          styles={customStyles}
        />
        <button className='button'  onClick={() => saveOrUpdateFilterPreference()}>Save/Update </button>
      </div> 
      <div style={{ marginTop: '15px', marginBottom: '60px', display: 'flex', columnGap: '10px' }}>
        <Select
          value={uniqueKPIs.find(option => option.value === selectedKPI)}
          onChange={handleKPIChange}
          options={uniqueKPIs}
          styles={customStyles}
          isClearable
          placeholder="KPI"
        />
        <Select
          value={uniqueGroup.find(option => option.value === selectedGrup)}
          onChange={handleGroupChange}
          options={uniqueGroup}
          styles={customStyles}
          isClearable
          placeholder="Group"
        />
        <Select
          value={selectedClient}
          onChange={setSelectedClient}
          options={clientoption}
          styles={customStyles}
          isClearable
          placeholder="Client"
        />
        <Select
          value={selectedSet}
          onChange={(value) => {
            if (!isKindMode) {
              setSelectedSet(value);
            }
          }}
          options={set}
          styles={customStyles}
          isClearable={!isKindMode}
          isDisabled={isKindMode}
          placeholder="set"
        />
        <Select
          value={selectedPosition}
          onChange={setSelectedPosition}
          options={positionOptions}
          isClearable
          placeholder="Filter by Position"
          styles={customStyles}
        />
        <Select
          value={selectedLocation}
          onChange={setSelectedLocation}
          options={locationOptions}
          isClearable
          placeholder="Filter by Location"
          styles={customStyles}
        />
        <Select
          value={selectedUser}
          onChange={setSelectedUser}
          options={userOptions}
          isClearable
          placeholder="Filter by User"
          styles={customStyles}
        />
      </div>

      <div className='content-wrapper content-wrapper-talent-list'>
        <div className='list-tasks-header'>
          <div style={{ flexBasis: '100px' }}>group</div>
          <div style={{ flexBasis: '150px' }}>start/end</div>
          <div style={{ flexBasis: '100px' }}>set</div>
          <div style={{ flexBasis: "150px" }}>studio location</div>
          <div style={{ flexBasis: '50px' }}>kpi</div>
          <div style={{ flexBasis: '100px' }}>pos.</div>
          <div style={{ flexBasis: '100px' }}>option</div>
          <div style={{ flexBasis: '150px' }}>talent</div>
          <div style={{ flexBasis: '200px' }}>client</div>
          <div style={{ flexBasis: '100px' }}>S.Info / Kind Info</div>
          <div style={{ flexBasis: '400px' }}></div>
        </div>
      </div>

      {Object.entries(groupedAndSortedEvents).map(([group, events]) => (
        <div key={group}>
          {events.map(event => (
            <div 
              className='list-tasks-item' 
              key={event.id}
              style={{
                borderLeft: (event.group_color || event.groupColor) ? `5px solid ${event.group_color || event.groupColor}` : 'none',
                paddingLeft: (event.group_color || event.groupColor) ? '15px' : '20px'
              }}
            >
              <div style={{ flexBasis: '100px' }}>
                <div 
                  style={{
                    backgroundColor: (event.group_color || event.groupColor) || 'transparent',
                    color: isColorDark(event.group_color || event.groupColor) ? '#ffffff' : '#000000',
                    padding: '4px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}
                >
                  {event.group || ''}
                </div>
              </div>

              <div style={{ flexBasis: '150px' }}>
                {event.start instanceof Date 
                  ? event.start.toLocaleDateString() 
                  : new Date(event.start).toLocaleDateString()
                } - {
                  event.end instanceof Date 
                  ? event.end.toLocaleDateString() 
                  : new Date(event.end).toLocaleDateString()
                }
              </div>
              <div style={{ flexBasis: '100px' }}>{event.set}</div>
              <div style={{ flexBasis: "150px" }}>
                {getStudioDisplay(event)}{getStudioDisplay(event) && getLocationDisplay(event) ? '/' : ''} {getLocationDisplay(event)}
              </div>
              <div style={{ flexBasis: '50px' }}>{getKPIDisplay(event)}</div>
              <div style={{ flexBasis: '100px' }}>{getPositionDisplay(event)}</div>
              <div style={{ flexBasis: '100px' }}>{getOptionDisplay(event)}</div>
              <div style={{ flexBasis: '150px' }}>{getTalentDisplay(event)}</div>
              <div style={{ flexBasis: '200px' }}>{getClientDisplay(event)}</div>
              <div style={{ flexBasis: '100px' }}>
                {(() => {
                  const sonstigeInfo = getSonstigeInfoDisplay(event);
                  const kindInfo = getKindInfoDisplay(event);
                  if (sonstigeInfo && kindInfo) {
                    return (
                      <>
                        <div>{sonstigeInfo}</div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>KIND: {kindInfo}</div>
                      </>
                    );
                  }
                  if (kindInfo) {
                    return <span style={{ fontSize: '11px', color: '#666' }}>KIND: {kindInfo}</span>;
                  }
                  return sonstigeInfo;
                })()}
              </div>
              <div style={{ marginLeft: 'auto',flexBasis: '400px' }}>
                {renderButtons(event)}
              </div>
            </div>
          ))}
        </div>
      ))}

      <h3 style={{backgroundColor:"#800080", color:'white', padding:'20px', }} >On Hold</h3>

      {Object.entries(groupedAndSortedHoldEvents).map(([group, events]) => (
        <div key={group}>
          {events.map(event => (
            <div 
              className='list-tasks-item' 
              key={event.id}
              style={{
                borderLeft: (event.group_color || event.groupColor) ? `5px solid ${event.group_color || event.groupColor}` : 'none',
                paddingLeft: (event.group_color || event.groupColor) ? '15px' : '20px'
              }}
            >
              <div style={{ flexBasis: '100px' }}>
                <div 
                  style={{
                    backgroundColor: (event.group_color || event.groupColor) || 'transparent',
                    color: isColorDark(event.group_color || event.groupColor) ? '#ffffff' : '#000000',
                    padding: '4px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}
                >
                  {event.group || ''}
                </div>
              </div>

              <div style={{ flexBasis: '200px' }}>
                {event.start instanceof Date 
                  ? event.start.toLocaleDateString() 
                  : new Date(event.start).toLocaleDateString()
                } - {
                  event.end instanceof Date 
                  ? event.end.toLocaleDateString() 
                  : new Date(event.end).toLocaleDateString()
                }
              </div>
              <div style={{ flexBasis: '100px' }}>{event.set}</div>
              <div style={{ flexBasis: "100px" }}>
                {getStudioDisplay(event)}{getStudioDisplay(event) && getLocationDisplay(event) ? '/' : ''} {getLocationDisplay(event)}
              </div>
              <div style={{ flexBasis: '50px' }}>{getKPIDisplay(event)}</div>
              <div style={{ flexBasis: '100px' }}>{getPositionDisplay(event)}</div>
              <div style={{ flexBasis: '100px' }}>{getOptionDisplay(event)}</div>
              <div style={{ flexBasis: '150px' }}>{getTalentDisplay(event)}</div>
              <div style={{ flexBasis: '200px' }}>{getClientDisplay(event)}</div>
              <div style={{ flexBasis: '100px' }}>
                {(() => {
                  const sonstigeInfo = getSonstigeInfoDisplay(event);
                  const kindInfo = getKindInfoDisplay(event);
                  if (sonstigeInfo && kindInfo) {
                    return (
                      <>
                        <div>{sonstigeInfo}</div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>KIND: {kindInfo}</div>
                      </>
                    );
                  }
                  if (kindInfo) {
                    return <span style={{ fontSize: '11px', color: '#666' }}>KIND: {kindInfo}</span>;
                  }
                  return sonstigeInfo;
                })()}
              </div>
              <div style={{ marginLeft: 'auto',flexBasis: '400px' }}>
                {renderButtonsforHold(event)}
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

export default ListViewProposed;
